import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Box,
  Typography,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  Collapse,
  Divider,
  Grid
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import trLocale from 'date-fns/locale/tr';
import enUS from 'date-fns/locale/en-US';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../../common/guide/GuideButton';
import type { InventoryItem } from '../types/inventory.types';
import { getCurrencySymbol } from '../utils/formatters';

interface UpdateValueDialogProps {
  open: boolean;
  onClose: () => void;
  selectedItem: InventoryItem | null;
  selectedField: 'quantity' | 'salePrice' | 'vatRate' | 'departmentNo' | 'name' | null;
  updateValue: number;
  onUpdateValueChange: (value: number) => void;
  updateStringValue: string;
  onUpdateStringValueChange: (value: string) => void;
  updateListPrice: number;
  onUpdateListPriceChange: (value: number) => void;
  isUpdating: boolean;
  onConfirm: (data?: {
    movementDirection?: 'IN' | 'OUT';
    referenceNumber?: string;
    description?: string;
    date?: Date;
    unitPrice?: number;
  }) => void;
  n11IntegrationActive: boolean;
  syncWithN11: boolean;
  onSyncWithN11Change: (checked: boolean) => void;
  isN11Syncing: boolean;
}

export const UpdateValueDialog: React.FC<UpdateValueDialogProps> = ({
  open,
  onClose,
  selectedItem,
  selectedField,
  updateValue,
  onUpdateValueChange,
  updateStringValue,
  onUpdateStringValueChange,
  updateListPrice,
  onUpdateListPriceChange,
  isUpdating,
  onConfirm,
  n11IntegrationActive,
  syncWithN11,
  onSyncWithN11Change,
  isN11Syncing,
}) => {
  const { i18n } = useTranslation();
  // startsWith: "tr-TR" gibi bölgeli kodlarda da doğru çalışsın (bkz. src/i18n/index.ts)
  const isTr = !(i18n.language || 'tr').toLowerCase().startsWith('en');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const dateLocale = isTr ? trLocale : enUS;
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const localizeUnit = (unit: string) => {
    if (isTr) return unit;
    const unitMap: Record<string, string> = {
      adet: 'pcs',
      çift: 'pair',
      paket: 'pack',
      kutu: 'box',
    };
    return unitMap[unit] || unit;
  };
  const updateValueInputRef = useRef<HTMLInputElement>(null);
  
  // Stok Hareketi için yeni state'ler
  const [movementDirection, setMovementDirection] = useState<'IN' | 'OUT' | ''>('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [movementDescription, setMovementDescription] = useState('');
  const [movementDate, setMovementDate] = useState<Date>(new Date());
  const [movementTime, setMovementTime] = useState<Date>(new Date());
  const [movementUnitPrice, setMovementUnitPrice] = useState(0);

  // Otomatik evrak numarası oluştur
  const generateReferenceNumber = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 9999).toString().padStart(4, '0');
    return `STK-${year}${month}${day}-${random}`;
  };

  // Modal açıldığında değerleri sıfırla
  useEffect(() => {
    if (open) {
      // ✅ Quantity field için varsayılan seçim YOK - kullanıcı seçmeli
      setMovementDirection(''); // Boş - kullanıcı seçecek
      
      setReferenceNumber(generateReferenceNumber()); // Otomatik evrak numarası
      setMovementDescription('');
      setMovementDate(new Date());
      setMovementTime(new Date());
      
      // Fiyatı ürüne göre ayarla
      if (selectedItem) {
        setMovementUnitPrice(selectedItem.purchasePrice || selectedItem.salePrice);
      }
      
      // Miktar alanını 0 yap
      if (selectedField === 'quantity') {
        onUpdateValueChange(0);
      }
      
      if (updateValueInputRef.current && selectedField !== 'quantity') {
        setTimeout(() => {
          updateValueInputRef.current?.select();
        }, 100);
      }
    }
  }, [open, selectedItem, selectedField, onUpdateValueChange]);
  
  // Direction değiştiğinde fiyatı güncelle
  useEffect(() => {
    if (selectedItem && movementDirection) {
      if (movementDirection === 'IN') {
        setMovementUnitPrice(selectedItem.purchasePrice || 0);
      } else if (movementDirection === 'OUT') {
        setMovementUnitPrice(selectedItem.salePrice || 0);
      }
    }
  }, [movementDirection, selectedItem]);
  
  // Toplam tutar hesaplama
  const totalAmount = updateValue * movementUnitPrice;
  
  // Mevcut miktar
  const currentQuantity = selectedItem?.warehouseStocks?.reduce((sum, ws) => sum + ws.quantity, 0) || 0;
  
  // Yeni miktar hesaplama
  const newQuantity = movementDirection === 'IN' 
    ? currentQuantity + updateValue 
    : movementDirection === 'OUT'
    ? currentQuantity - updateValue
    : currentQuantity;

  return (
    <Dialog
      open={open}
      onClose={!isUpdating ? onClose : undefined}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {selectedField === 'quantity' ? (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{tt('Stok Hareketi', 'Stock Movement')}</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <GuideButton guideKey="inventory.stockMovement" size="small" />
              <Select
                value={movementDirection}
                onChange={(e) => setMovementDirection(e.target.value as 'IN' | 'OUT' | '')}
                size="small"
                displayEmpty
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="" disabled>
                  <em>{tt('Seçiniz...', 'Select...')}</em>
                </MenuItem>
                <MenuItem value="IN">📥 {tt('Stok Giriş', 'Stock Entry')}</MenuItem>
                <MenuItem value="OUT">📤 {tt('Stok Çıkış', 'Stock Exit')}</MenuItem>
              </Select>
            </Box>
          </Box>
        ) : (
          selectedField === 'salePrice' ? tt('Satış Fiyatı Güncelle', 'Update Sale Price') :
          selectedField === 'vatRate' ? tt('KDV Oranı Güncelle', 'Update VAT Rate') :
          selectedField === 'departmentNo' ? tt('ÖKC (Departman No) Güncelle', 'Update Cash Register Dept. No') :
          selectedField === 'name' ? tt('Ürün Adı Güncelle', 'Update Product Name') : tt('Güncelle', 'Update')
        )}
      </DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          {/* Miktar alanı için - Sadece direction seçiliyse göster */}
          {selectedField === 'quantity' && (
            <>
              {/* Ürün Bilgileri */}
              {selectedItem && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {tt('Ürün', 'Product')}: <strong>{selectedItem.name}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {tt('Stok Kodu', 'Stock Code')}: <strong>{selectedItem.stockCode}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {tt('Mevcut Miktar', 'Current Quantity')}: <strong>{currentQuantity.toLocaleString(localeCode)} {localizeUnit(selectedItem.unit)}</strong>
                  </Typography>
                </Box>
              )}
              
              {/* Stok Hareketi Formu - Sadece direction seçiliyse göster */}
              <Collapse in={movementDirection !== ''}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Tarih ve Saat - En Üstte, Yan Yana */}
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <DateTimePicker
                          label={tt('Tarih', 'Date')}
                          value={movementDate}
                          onChange={(newValue) => setMovementDate(newValue || new Date())}
                          format="dd.MM.yyyy"
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              required: true,
                              size: 'medium'
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <DateTimePicker
                          label={tt('Saat', 'Time')}
                          value={movementTime}
                          onChange={(newValue) => setMovementTime(newValue || new Date())}
                          format="HH:mm"
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              required: true,
                              size: 'medium'
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </LocalizationProvider>
                  
                  <TextField
                    required
                    fullWidth
                    id="quantity"
                    label={tt('Miktar', 'Quantity')}
                    name="quantity"
                    autoFocus
                    type="number"
                    value={updateValue}
                    onChange={(e) => onUpdateValueChange(parseFloat(e.target.value) || 0)}
                    InputLabelProps={{ shrink: true }}
                    inputRef={updateValueInputRef}
                    onFocus={(e) => e.target.select()}
                    InputProps={{
                      endAdornment: selectedItem ? (
                        <InputAdornment position="end">
                          {localizeUnit(selectedItem.unit)}
                        </InputAdornment>
                      ) : undefined
                    }}
                    helperText={
                      movementDirection === 'OUT' && updateValue > currentQuantity
                        ? tt(`⚠️ Mevcut stoktan (${currentQuantity}) fazla çıkış yapılamaz!`, `⚠️ You cannot exit more than current stock (${currentQuantity})!`)
                        : ''
                    }
                    error={movementDirection === 'OUT' && updateValue > currentQuantity}
                  />
                  
                  <TextField
                    fullWidth
                    id="referenceNumber"
                    label={tt('Evrak Numarası', 'Document Number')}
                    name="referenceNumber"
                    type="text"
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    placeholder="STK-20241219-0001"
                    helperText={tt('Otomatik oluşturuldu, değiştirebilirsiniz', 'Auto-generated, you can change it')}
                  />
                  
                  <TextField
                    fullWidth
                    id="movementDescription"
                    label={tt('Açıklama', 'Description')}
                    name="movementDescription"
                    type="text"
                    multiline
                    rows={2}
                    value={movementDescription}
                    onChange={(e) => setMovementDescription(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    placeholder={
                      movementDirection === 'IN' 
                        ? tt('Örn: Tedarikçiden gelen mal...', 'Ex: Goods received from supplier...')
                        : tt('Örn: Fire, kayıp, bozulma...', 'Ex: Waste, loss, spoilage...')
                    }
                  />
                  
                  <Box>
                    <TextField
                      required
                      fullWidth
                      id="movementUnitPrice"
                      label={movementDirection === 'IN' ? tt('Alış Fiyatı', 'Purchase Price') : tt('Satış Fiyatı', 'Sale Price')}
                      name="movementUnitPrice"
                      type="number"
                      value={movementUnitPrice}
                      onChange={(e) => setMovementUnitPrice(parseFloat(e.target.value) || 0)}
                      InputLabelProps={{ shrink: true }}
                      onFocus={(e) => e.target.select()}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {selectedItem?.saleCurrency ? getCurrencySymbol(selectedItem.saleCurrency) : '₺'}
                          </InputAdornment>
                        )
                      }}
                    />
                    <Typography variant="body2" color="primary" sx={{ mt: 1, fontWeight: 'bold' }}>
                      {tt('Toplam Tutar', 'Total Amount')}: {totalAmount.toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {selectedItem?.saleCurrency ? getCurrencySymbol(selectedItem.saleCurrency) : '₺'}
                    </Typography>
                  </Box>
                  
                  <Divider />
                  
                  {/* Özet Bilgi */}
                  <Box sx={{ p: 2, bgcolor: movementDirection === 'IN' ? 'success.light' : 'warning.light', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight="bold" color={movementDirection === 'IN' ? 'success.dark' : 'warning.dark'}>
                      {movementDirection === 'IN' ? '✅' : '⚠️'} 
                      {' '}
                      {movementDirection === 'IN' ? tt('Stok Giriş', 'Stock Entry') : tt('Stok Çıkış', 'Stock Exit')}:
                      {' '}
                      {movementDirection === 'IN' ? '+' : '-'}{updateValue.toLocaleString(localeCode)} {selectedItem?.unit ? localizeUnit(selectedItem.unit) : ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tt('Mevcut', 'Current')}: {currentQuantity.toLocaleString(localeCode)} → {tt('Yeni', 'New')}: {newQuantity.toLocaleString(localeCode)} {selectedItem?.unit ? localizeUnit(selectedItem.unit) : ''}
                    </Typography>
                  </Box>
                </Box>
              </Collapse>
              
              {/* Direction seçilmemişse uyarı */}
              {movementDirection === '' && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="info.dark">
                    {tt('ℹ️ Lütfen yukarıdan "Stok Giriş" veya "Stok Çıkış" seçiniz.', 'ℹ️ Please select "Stock Entry" or "Stock Exit" above.')}
                  </Typography>
                </Box>
              )}
            </>
          )}
          
          {/* Ürün Adı için String Input */}
          {selectedField === 'name' && (
            <TextField
              margin="normal"
              required
              fullWidth
              id="updateStringValue"
              label={tt('Yeni Ürün Adı', 'New Product Name')}
              name="updateStringValue"
              autoFocus
              type="text"
              value={updateStringValue}
              onChange={(e) => onUpdateStringValueChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputRef={updateValueInputRef}
              onFocus={(e) => e.target.select()}
              helperText={tt('Ürünün yeni adını girin', 'Enter the new product name')}
            />
          )}
          
          {/* Diğer alanlar için (salePrice, vatRate, departmentNo) */}
          {selectedField !== 'quantity' && selectedField !== 'name' && (
            <>
              <TextField
                margin="normal"
                required
                fullWidth
                id="updateValue"
                label={
                  selectedField === 'salePrice' ? tt('Yeni Satış Fiyatı', 'New Sale Price') :
                  selectedField === 'vatRate' ? tt('Yeni KDV Oranı', 'New VAT Rate') :
                  selectedField === 'departmentNo' ? tt('Yeni Departman No (ÖKC)', 'New Cash Register Dept. No') : tt('Yeni Değer', 'New Value')
                }
                name="updateValue"
                autoFocus
                type="number"
                value={updateValue}
                onChange={(e) => onUpdateValueChange(parseFloat(e.target.value) || 0)}
                InputLabelProps={{ shrink: true }}
                inputRef={updateValueInputRef}
                onFocus={(e) => e.target.select()}
                InputProps={{
                  endAdornment: selectedField === 'salePrice' && selectedItem ? (
                    <InputAdornment position="end">
                      {selectedItem.saleCurrency ? getCurrencySymbol(selectedItem.saleCurrency) : 'TL'}
                    </InputAdornment>
                  ) : selectedField === 'vatRate' ? (
                    <InputAdornment position="end">%</InputAdornment>
                  ) : undefined
                }}
                helperText={
                  selectedField === 'vatRate' ? tt('KDV oranını yüzde olarak girin (örn: 18)', 'Enter VAT rate as percentage (e.g. 18)') :
                  selectedField === 'departmentNo' ? tt('Ödeme Kaydedici Cihaz departman numarası', 'Cash register department number') : undefined
                }
              />
              
              {/* Liste Fiyatı Alanı - Sadece satış fiyatı güncellenirken göster */}
              {selectedField === 'salePrice' && (
                <TextField
                  fullWidth
                  margin="normal"
                  id="updateListPrice"
                  label={tt('Liste Fiyatı (Opsiyonel)', 'List Price (Optional)')}
                  name="updateListPrice"
                  type="number"
                  value={updateListPrice}
                  onChange={(e) => onUpdateListPriceChange(parseFloat(e.target.value) || 0)}
                  InputLabelProps={{ shrink: true }}
                  onFocus={(e) => e.target.select()}
                  InputProps={{
                    endAdornment: selectedItem ? (
                      <InputAdornment position="end">
                        {getCurrencySymbol(selectedItem.saleCurrency || 'TRY')}
                      </InputAdornment>
                    ) : null
                  }}
                  helperText={tt('N11, Trendyol gibi pazaryerleri için liste fiyatı. Boş bırakılabilir.', 'List price for marketplaces like N11 and Trendyol. Can be left empty.')}
                />
              )}
              
              {selectedItem && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <>
                    {tt('Ürün', 'Product')}: <b>{selectedItem.name}</b>
                    <br />
                    {tt('Mevcut', 'Current')} {
                      selectedField === 'salePrice' ? tt('Satış Fiyatı', 'Sale Price') :
                      selectedField === 'vatRate' ? tt('KDV Oranı', 'VAT Rate') :
                      selectedField === 'departmentNo' ? tt('Departman No', 'Department No') : tt('Değer', 'Value')
                    }: <b>
                      {selectedField === 'salePrice'
                        ? `${selectedItem.salePrice.toLocaleString(localeCode)} ${selectedItem.saleCurrency ? getCurrencySymbol(selectedItem.saleCurrency) : tt('TL', 'TRY')}`
                        : selectedField === 'vatRate'
                        ? `%${selectedItem.vatRate || 0}`
                        : selectedField === 'departmentNo'
                        ? (selectedItem.departmentNo !== null && selectedItem.departmentNo !== undefined ? selectedItem.departmentNo : tt('Tanımlı değil', 'Not defined'))
                        : '-'
                      }
                    </b>
                  </>
                  {(selectedItem as any).isQRMenu === true && (selectedField === 'salePrice') && (
                    <>
                      <br />
                      <Typography variant="caption" color="success.main" sx={{ fontWeight: 'bold' }}>
                        {tt('✅ Bu ürün QR Menü sisteminde de güncellenecektir', '✅ This product will also be updated in the QR Menu system')}
                      </Typography>
                    </>
                  )}
                </Typography>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose} 
          color="inherit"
          disabled={isUpdating}
        >
          {tt('İptal', 'Cancel')}
        </Button>
        <Button 
          onClick={() => {
            if (selectedField === 'quantity' && movementDirection) {
              // Stok hareketi için ek bilgileri gönder
              onConfirm({
                movementDirection,
                referenceNumber,
                description: movementDescription,
                date: movementDate,
                unitPrice: movementUnitPrice
              });
            } else {
              // Diğer field'lar için normal şekilde gönder
              onConfirm();
            }
          }} 
          variant="contained"
          color="primary" 
          disabled={
            isUpdating || 
            isN11Syncing || 
            (selectedField === 'quantity' && movementDirection === '') ||
            (selectedField === 'quantity' && movementDirection === 'OUT' && updateValue > currentQuantity)
          }
          startIcon={(isUpdating || isN11Syncing) ? <CircularProgress size={24} /> : undefined}
        >
          {isUpdating ? tt('Kaydediliyor...', 'Saving...') : isN11Syncing ? tt('N11 Senkronizasyonu...', 'N11 Synchronizing...') : tt('Kaydet', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

